/**
 * Build authenticated preview/download URLs for local uploads and MinIO objects.
 */

function isObjectStorageUrl(fileUrl: string): boolean {
  if (!fileUrl) return false;
  if (fileUrl.includes(':9000/')) return true;
  if (fileUrl.includes('pregnancy-monitoring-')) return true;
  if (fileUrl.startsWith('/local/')) return true;
  return false;
}

export function getFilePreviewUrl(fileUrl?: string | null): string {
  if (!fileUrl) {
    return '';
  }

  const token = localStorage.getItem('access_token');
  const origin = window.location.origin;

  if (fileUrl.startsWith(`${origin}/api/v1/files/preview`)) {
    if (token && !fileUrl.includes('token=')) {
      const separator = fileUrl.includes('?') ? '&' : '?';
      return `${fileUrl}${separator}token=${encodeURIComponent(token)}`;
    }
    return fileUrl;
  }

  // MinIO / S3 / legacy object URLs — always go through API proxy (browser cannot reach :9000)
  if (isObjectStorageUrl(fileUrl) || fileUrl.startsWith('/uploads/') || (!fileUrl.startsWith('http'))) {
    const params = new URLSearchParams({ file_url: fileUrl });
    if (token) {
      params.set('token', token);
    }
    return `${origin}/api/v1/files/preview?${params.toString()}`;
  }

  // Other absolute http(s) — leave as-is
  if (fileUrl.startsWith('http://') || fileUrl.startsWith('https://')) {
    return fileUrl;
  }

  const params = new URLSearchParams({ file_url: fileUrl });
  if (token) {
    params.set('token', token);
  }
  return `${origin}/api/v1/files/preview?${params.toString()}`;
}

/** Suggested download filename from stored path. */
export function getDownloadFileName(fileUrl: string, baseName: string): string {
  const match = fileUrl.match(/\.([a-zA-Z0-9]+)(?:\?|$)/);
  const ext = match?.[1] || 'bin';
  return `${baseName}.${ext}`;
}

/** Extract File/Blob from Ant Design UploadFile (Safari/Form.Item safe). */
export function getUploadRawFile(file: any): File | Blob | null {
  if (!file) return null;
  // beforeUpload: false — antd may pass the native File directly
  if (typeof File !== 'undefined' && file instanceof File) return file;
  if (typeof Blob !== 'undefined' && file instanceof Blob) return file;
  const raw = file.originFileObj || file;
  if (typeof File !== 'undefined' && raw instanceof File) return raw;
  if (typeof Blob !== 'undefined' && raw instanceof Blob) return raw;
  return null;
}

/** Append upload to FormData with an explicit filename (required by FastAPI UploadFile). */
export function appendUploadToFormData(
  formData: FormData,
  fieldName: string,
  upload: any,
  fallbackName = 'upload.bin'
): boolean {
  const raw = getUploadRawFile(upload);
  if (!raw) return false;
  const name =
    (typeof File !== 'undefined' && raw instanceof File && raw.name) ||
    upload?.name ||
    fallbackName;
  formData.append(fieldName, raw, name);
  return true;
}
